import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";

import Modal from "components/Modal";
import SearchInput from "components/SearchInput";
import SegmentTabs from "components/SegmentTabs";
import Select from "components/ToolsPageLayout/ToolsPageSmartScoreScreener/Components/Select";
import useActivityTracker from "hooks/useActivityTracker";
import { fetchApi } from "utils/baseFetcher";

import { RecentSmartscriptSection } from "../AIWorkflowModalSections";

import WhySmartscripts from "./WhySmartscripts";

const MIN_EXPERT_LENGTH = 20;
const MIN_SPECIAL_INSTRUCTION_LENGTH = 50;

type SearchEntityModalProps = {
  onClose?: () => void;
  contentOnly?: boolean;
  loading: boolean;
  onGenerateStart: () => void;
  onGenerated: (_transcriptId?: string | number) => void;
  onGenerateError: () => void;
  entity?: any;
  onOpenTranscript?: (_transcriptId: string | number) => void;
  showWhySmartscript?: boolean;
};

const SearchEntityModal = ({
  loading,
  contentOnly,
  onClose,
  onGenerateStart,
  onGenerated,
  onGenerateError,
  entity: defaultEntity,
  onOpenTranscript,
  showWhySmartscript = true,
}: SearchEntityModalProps) => {
  const [selectedCaller, setSelectedCaller] = useState<any>();
  const [otherCaller, setOtherCaller] = useState<string>();
  const [expert, setExpert] = useState<string>();
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [callLength, setCallLength] = useState<any>({
    title: "30 Minutes",
    name: 30,
    id: "2",
  });
  const [granularity, setGranularity] = useState<any>({
    title: "Moderate",
    name: "moderate",
    id: "1",
  });
  const [specialInstruction, setSpecialInstruction] = useState<string>("");
  const [polishing, setPolishing] = useState<boolean>(false);
  const [polishedKey, setPolishedKey] = useState<string | null>(null);
  const { trackActivity } = useActivityTracker();

  const expertLength = expert?.trim().length ?? 0;
  const instructionLength = specialInstruction.trim().length;

  const requiredFieldMessages = useMemo(() => {
    const messages: string[] = [];
    if (!selectedCaller) {
      messages.push("Please select a Caller.");
    } else if (selectedCaller.value === "other" && !otherCaller?.trim()) {
      messages.push("Please describe the Caller.");
    }
    if (!expertLength) {
      messages.push("Please describe the Expert.");
    }
    if (!selectedEntity) {
      messages.push("Please select a Company or ticker to discuss.");
    }
    if (!instructionLength) {
      messages.push("Please write Special instructions.");
    }
    return messages;
  }, [
    selectedCaller,
    otherCaller,
    expertLength,
    selectedEntity,
    instructionLength,
  ]);

  const isExpertTooShort = expertLength > 0 && expertLength < MIN_EXPERT_LENGTH;
  const isInstructionTooShort =
    instructionLength > 0 && instructionLength < MIN_SPECIAL_INSTRUCTION_LENGTH;

  const validationMessages = useMemo(() => {
    const messages = [...requiredFieldMessages];
    if (isExpertTooShort) {
      messages.push(
        `Please describe the Expert in at least ${MIN_EXPERT_LENGTH} characters (${expertLength}/${MIN_EXPERT_LENGTH}).`
      );
    }
    if (isInstructionTooShort) {
      messages.push(
        `Please write Special instructions of at least ${MIN_SPECIAL_INSTRUCTION_LENGTH} characters (${instructionLength}/${MIN_SPECIAL_INSTRUCTION_LENGTH}).`
      );
    }
    return messages;
  }, [
    requiredFieldMessages,
    isExpertTooShort,
    isInstructionTooShort,
    expertLength,
    instructionLength,
  ]);

  const isFormValid = validationMessages.length === 0;

  const polishPayload = useMemo(
    () => ({
      expert_type: expert,
      entity_name: selectedEntity?.prettyName,
      special_instructions: specialInstruction,
      caller_type: selectedCaller?.value,
      call_length: callLength.name,
    }),
    [
      callLength.name,
      expert,
      selectedCaller?.value,
      selectedEntity?.prettyName,
      specialInstruction,
    ]
  );

  // Polish only needs the payload fields to be present — the minimum lengths
  // are a generate-time requirement, and polishing is what helps the user meet
  // them.
  const canPolish = requiredFieldMessages.length === 0;
  const isPolished =
    polishedKey !== null && polishedKey === JSON.stringify(polishPayload);

  const polishInstructions = useCallback(async (payload: any) => {
    const response = await fetchApi("/smartscript/api/polish-instructions", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return response?.polished || payload.special_instructions;
  }, []);

  const onPolish = useCallback(async () => {
    setPolishing(true);
    try {
      const polished = await polishInstructions(polishPayload);
      setSpecialInstruction(polished);
      setPolishedKey(
        JSON.stringify({ ...polishPayload, special_instructions: polished })
      );
    } catch (error) {
      console.log(error);
    } finally {
      setPolishing(false);
    }
  }, [polishInstructions, polishPayload]);

  const onEntitySearchSelected = useCallback((entity: Entity) => {
    setSelectedEntity(entity);
  }, []);

  const onGenerate = useCallback(async () => {
    onGenerateStart();

    try {
      const instructions = isPolished
        ? specialInstruction
        : await polishInstructions(polishPayload);

      const data = {
        ...polishPayload,
        caller_custom: otherCaller,
        entity_id: selectedEntity?.id,
        bloomberg_ticker: selectedEntity?.shortSecurity,
        special_instructions: instructions,
        origin: process.env.APP_ENV === "staging" ? "test" : undefined,
      };

      const response = await fetchApi("/smartscript/api/generate-transcript", {
        method: "POST",
        body: JSON.stringify(data),
      });

      trackActivity("smartscipts.generate", null, data);

      onGenerated(response?.id || response?.detail?.transcript_id);
    } catch (error) {
      console.log(error);
      onGenerateError();
    }
  }, [
    isPolished,
    onGenerateError,
    onGenerateStart,
    onGenerated,
    otherCaller,
    polishInstructions,
    polishPayload,
    selectedEntity?.id,
    selectedEntity?.shortSecurity,
    specialInstruction,
    trackActivity,
  ]);

  useEffect(() => {
    if (defaultEntity) {
      setSelectedEntity(defaultEntity);
    }
  }, [defaultEntity]);

  const content = useMemo(() => {
    return (
      <div className={twMerge(contentOnly && "flex items-start gap-[15px]")}>
        <div className={twMerge(contentOnly && "w-3/4")}>
          <div className="mb-[20px] flex items-start justify-between">
            <div className="flex items-center gap-[10px]">
              <div className="flex h-[32px] w-[32px] items-center justify-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="https://assets.smartkarma.com/icons/sparkle.svg"
                  alt="AI Workflows"
                  width={20}
                  height={20}
                />
              </div>
              <div>
                <div className="text-[16px] font-semibold leading-tight text-[#1f2937]">
                  Generate SmartScript
                </div>
                <div className="mt-[2px] text-[12px] text-[#6b7280]">
                  Powered by Smartkarma AI
                </div>
              </div>
            </div>
          </div>

          {showWhySmartscript && <WhySmartscripts className="mb-[20px]" />}

          <p className="font-medium text-[18px]">
            I want a Synthetic Expert Network Call Transcript between:
          </p>

          <div
            className={twMerge(
              "flex flex-col md:flex-row md:items-start gap-[15px]",
              selectedCaller?.value === "other" && "items-start"
            )}
          >
            <div className="flex-1">
              <label className="mb-[6px] block text-[13px] font-medium text-[#1f2937]">
                User A - (Caller)
              </label>
              <Select
                placement={"bottom"}
                showSelectionCountZero={false}
                label={selectedCaller ? selectedCaller.label : "Select Caller"}
                options={[
                  {
                    label: "Buy-side analyst",
                    value: "Buyside analyst",
                  },
                  {
                    label: "Sell-side analyst",
                    value: "sellside analyst",
                  },
                  {
                    label: "Corporate Strategy",
                    value: "corporate Strategy",
                  },
                  {
                    label: "Other",
                    value: "other",
                  },
                ]}
                selected={selectedCaller}
                className="!shadow-none border-[1px] border-solid border-[#d1d5db] mb-[10px] !rounded-md"
                activeClassName="!shadow-sm !shadow-smart-teal/50 border-smart-teal"
                containerClassName="w-full shadow-none"
                optionContainerClassName="w-full z-[10000]"
                caretClassName="text-smart-teal"
                optionListContainerClassName="!max-h-none !overflow-y-hidden"
                onSelect={(selected) => {
                  setSelectedCaller(selected);
                }}
              />
              {selectedCaller?.value === "other" && (
                <input
                  className="w-full rounded-md !outline-none focus:!shadow-sm focus:!shadow-smart-teal mb-[10px] focus:!outline-none !ring-0 !px-[10px] py-[8px] text-[14px] bg-transparent"
                  placeholder={`Describe the caller`}
                  value={otherCaller}
                  maxLength={80}
                  onChange={(e) => {
                    setOtherCaller(e.target.value);
                  }}
                />
              )}
            </div>

            <div className="flex-1">
              <label className="mb-[6px] block text-[13px] font-medium text-[#1f2937]">
                User B - (Expert)
              </label>
              <input
                className="w-full rounded-md !outline-none focus:!shadow-sm focus:!shadow-smart-teal focus:!outline-none !ring-0 !px-[10px] py-[8px] text-[14px] bg-transparent"
                placeholder={`e.g Former VP of Sales at SAP`}
                value={expert}
                maxLength={80}
                onChange={(e) => {
                  setExpert(e.target.value);
                }}
              />
              <p
                className={twMerge(
                  "mb-[10px] mt-[4px] text-[10px] text-[#6b7280]",
                  isExpertTooShort && "text-bearish-red"
                )}
              >
                Describe the expert in detail — at least {MIN_EXPERT_LENGTH}{" "}
                characters ({expertLength}/{MIN_EXPERT_LENGTH}).
              </p>
            </div>
          </div>

          <div className="">
            <label className="mb-[6px] block text-[13px] font-medium text-[#1f2937]">
              To Discuss — Search for a Company or Ticker
            </label>
            <SearchInput
              resultPlaceholder="Search & select from the list or type free text only if not found."
              placeholder={
                selectedEntity?.prettyName ||
                "Search by name or ticker (e.g. Tencent, 700 HK) or type free text"
              }
              endpoint="/api/v2/entities"
              queryParam="filter[fulltext]"
              editableValue={
                selectedEntity?.isCustom
                  ? selectedEntity?.prettyName
                  : undefined
              }
              onItemSelected={onEntitySearchSelected}
              className="mb-[10px] w-full rounded-[6px] border-[1px] border-solid border-[#d1d5db]"
              inputClassName={(isFocusing) =>
                twMerge(
                  isFocusing ? "" : "border-0",
                  "rounded-[6px] py-[20px] px-0 placeholder:text-[13px]"
                )
              }
              iconClassName={() => "text-[#9ca3af]"}
              dropdownClassName="top-[33px] rounded-b-[6px]"
              processData={({ data }) => ({
                data: data.filter((e: Entity) => !e.shouldHidePrimer),
              })}
              customListHeader={({ query, onItemClick }) =>
                query.length <= 1 ? null : (
                  <div
                    className={twMerge(
                      "truncate hover:bg-smart-teal hover:text-white",
                      "px-[7px] py-[3px] cursor-pointer",
                      "group flex items-center",
                      "italic"
                    )}
                    onClick={() =>
                      onItemClick({
                        prettyName: query,
                        shortName: query,
                        isCustom: true,
                      })
                    }
                  >
                    + Use &quot;{query}&quot; to discuss
                  </div>
                )
              }
              disableEmptyMessage
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-[15px]">
            <div className="flex-1">
              <label className="mb-[6px] block text-[13px] font-medium text-[#1f2937]">
                Call Length
              </label>
              <SegmentTabs
                tabs={[
                  {
                    title: "15 Minutes",
                    name: 15,
                    id: "1",
                  },
                  {
                    title: "30 Minutes",
                    name: 30,
                    id: "2",
                  },
                  {
                    title: "60 Minutes",
                    name: 60,
                    id: "3",
                  },
                ]}
                activeTabId={callLength?.id}
                onChangeTab={(selected) => {
                  setCallLength(selected);
                }}
                itemClassName="py-[5px] !border border-solid border-transparent"
                className="mt-[10px] mb-[5px]"
                activeClassName="!border border-solid !border-smart-teal text-smart-teal"
              />
              <p className="mb-0 text-[10px] text-[#6b7280]">
                Longer calls are more in-depth, but take a little longer to
                generate.
              </p>
            </div>
            <div className="flex-1">
              <label className="mb-[6px] block text-[13px] font-medium text-[#1f2937]">
                Granularity
              </label>
              <SegmentTabs
                tabs={[
                  {
                    title: "Moderate",
                    name: "moderate",
                    id: "1",
                  },
                  {
                    title: "High",
                    name: "high",
                    id: "2",
                  },
                ]}
                activeTabId={granularity?.id}
                onChangeTab={(selected) => {
                  setGranularity(selected);
                }}
                itemClassName="py-[5px] !border border-solid border-transparent"
                className="mt-[10px] mb-[5px]"
                activeClassName="!border border-solid !border-smart-teal text-smart-teal"
              />
              <p className="mb-0 text-[10px] text-[#6b7280]">
                High checks live sources — more precise, but slower.
              </p>
            </div>
          </div>

          <div className="flex-1 mt-[10px]">
            <div className="mb-[6px] flex items-center justify-between gap-[10px]">
              <label className="block text-[13px] font-medium text-[#1f2937]">
                Special Instructions
              </label>
            </div>
            <textarea
              className={twMerge(
                "w-full min-h-[120px] p-[10px]",
                "border border-solid border-[#ddd] rounded-[6px]",
                "text-[14px] resize-y",
                "focus:outline-none focus:border-smart-teal"
              )}
              value={specialInstruction}
              onChange={(e) => {
                setSpecialInstruction(e.target.value);
              }}
            />
            <div className="flex items-center gap-[10px]">
              <p
                className={twMerge(
                  "mb-0 text-[12px] text-[#6b7280]",
                  isInstructionTooShort && "text-bearish-red"
                )}
              >
                Describe the focus of the call — at least{" "}
                {MIN_SPECIAL_INSTRUCTION_LENGTH} characters ({instructionLength}
                /{MIN_SPECIAL_INSTRUCTION_LENGTH}).
              </p>
              <OverlayTrigger
                placement="top"
                overlay={
                  !canPolish ? (
                    <Tooltip>
                      <p className="mb-0">{requiredFieldMessages[0]}</p>
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <span className={!canPolish ? "cursor-not-allowed" : ""}>
                  <button
                    type="button"
                    onClick={onPolish}
                    disabled={!canPolish || polishing || isPolished}
                    className={twMerge(
                      "rounded-full border border-solid px-[12px] py-[4px] text-[12px] font-medium",
                      !canPolish
                        ? "pointer-events-none border-shady-gray bg-white text-shady-gray"
                        : isPolished
                        ? "cursor-default border-transparent bg-smart-teal/10 text-smart-teal"
                        : "cursor-pointer border-smart-teal bg-white text-smart-teal hover:bg-smart-teal hover:text-white",
                      polishing && "animate-pulse"
                    )}
                  >
                    {isPolished
                      ? "✓ Polished"
                      : polishing
                      ? "Polishing..."
                      : "✨ Polish"}
                  </button>
                </span>
              </OverlayTrigger>
            </div>
          </div>

          <p className="my-[18px] text-[12px] text-[#6b7280]">
            If a recent SmartScript exists, we&apos;ll open it instead of
            regenerating.
          </p>

          <div className="mb-[20px] rounded-[6px] bg-[#f9fafb] px-[14px] py-[12px]">
            <div className="mb-[8px] text-[12px] font-medium text-[#1f2937]">
              Your SmartScript will include
            </div>
            <div className="flex flex-col gap-[6px]">
              {[
                "15-60 min expert call transcript",
                "Tailored to your caller persona & focus areas",
                "Probing follow-ups with quantified analysis",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-[8px] text-[12px] text-[#374151]"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1D9E75"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-[12px]">
            <div className="flex items-center gap-[6px] text-[12px] text-[#6b7280]">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9ca3af"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              ~30-60 seconds
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer rounded-full border border-solid border-[#d1d5db] bg-white px-[14px] py-[8px] text-[13px] font-medium text-[#374151] hover:bg-gray-50"
              >
                Cancel
              </button>
              <OverlayTrigger
                placement="top"
                overlay={
                  !isFormValid && !loading ? (
                    <Tooltip>
                      <p className="mb-0">{validationMessages[0]}</p>
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <span className={!isFormValid ? "cursor-not-allowed" : ""}>
                  <button
                    type="button"
                    onClick={onGenerate}
                    disabled={!isFormValid || loading}
                    className={twMerge(
                      "rounded-full border-none px-[16px] py-[8px] text-[13px] font-medium",
                      isFormValid
                        ? "cursor-pointer bg-smart-teal-300 text-white hover:bg-dark-teal"
                        : "pointer-events-none bg-smart-teal/50 text-white opacity-50",
                      loading && "animate-pulse"
                    )}
                  >
                    {!loading ? "Generate" : "Generating..."}
                  </button>
                </span>
              </OverlayTrigger>
            </div>
          </div>
        </div>

        <div className={twMerge(contentOnly && "flex-1")}>
          <RecentSmartscriptSection
            contentOnly={contentOnly}
            onSelectSmartscript={onOpenTranscript}
          />
        </div>
      </div>
    );
  }, [
    callLength?.id,
    canPolish,
    contentOnly,
    expert,
    expertLength,
    granularity?.id,
    instructionLength,
    isExpertTooShort,
    isFormValid,
    isInstructionTooShort,
    isPolished,
    loading,
    onClose,
    onEntitySearchSelected,
    onGenerate,
    onOpenTranscript,
    onPolish,
    otherCaller,
    polishing,
    requiredFieldMessages,
    selectedCaller,
    selectedEntity?.isCustom,
    selectedEntity?.prettyName,
    showWhySmartscript,
    specialInstruction,
    validationMessages,
  ]);

  if (contentOnly) {
    return content;
  }

  return (
    <Modal
      isOpen
      shouldCloseOnEsc
      onClose={onClose}
      className={twMerge("lg:w-[720px] xl:w-[920px] md:!my-[50px]")}
      overlayClassName=""
      onAfterOpen={() =>
        document.body.classList.add("!max-h-[calc(100vh-100px)]")
      }
      onAfterClose={() =>
        document.body.classList.remove("!max-h-[calc(100vh-100px)]")
      }
      fullMobilePage
    >
      <div className="p-[24px]">{content}</div>
    </Modal>
  );
};

export default SearchEntityModal;
